import { useCallback, useEffect, useMemo, useState } from "react";

import Button from "@brighthr/component-button";
import { ReactComponent as ErrorIconSvg } from "shared/assets/Error_Icon.svg";
import { ReactComponent as InfoIconSvg } from "shared/assets/Info_Icon.svg";
import { ReactComponent as CancelIconSvg } from "shared/assets/Icons/cancel.svg";
import { Modal } from "shared/UI/Modal/Modal";
import theme from "../../../../../theme";
import { useModalContext } from "core/state/ModalContext/ModalContext";
import classNames from "classnames";

export interface StatusBody {
  data: {
    title: string;
    description: string;
    cta: string;
    dismissCta?: string;
  };
  status: "success" | "error" | "info";
  dismissCallback?: () => void;
  cancelCallback?: () => void;
  callback?: () => void;
  ignoreHide?: boolean;
  showDismiss?: boolean;
}

export const useStatusModal = (prefix?: string) => {
  const [body, setBody] = useState<StatusBody | null>(null);
  const { isVisible: isVisibleError, hide: hideError, show: showError } = useModalContext(`${prefix ?? ""}-error-modal`);
  const { isVisible: isVisibleInfo, hide: hideInfo, show: showInfo } = useModalContext(`${prefix ?? ""}-info-modal`);
  const { isVisible: isVisibleSuccess, hide: hideSuccess, show: showSuccess } = useModalContext(`${prefix ?? ""}-success-modal`);

  const handleCallbacks = useCallback(
    (callback?: () => void) => {
      if (!body?.ignoreHide) {
        body?.status === "info" && hideInfo();
        body?.status === "error" && hideError();
        body?.status === "success" && hideSuccess();
      }
      callback && callback();
    },
    [body?.ignoreHide, body?.status, hideError, hideInfo, hideSuccess]
  );

  const handleCancel = useCallback(() => handleCallbacks(body?.cancelCallback), [body?.cancelCallback, handleCallbacks]);

  const handleDismiss = useCallback(() => handleCallbacks(body?.dismissCallback), [body?.dismissCallback, handleCallbacks]);

  const handleConfirm = useCallback(() => handleCallbacks(body?.callback), [body?.callback, handleCallbacks]);

  const modalComponent = useMemo(
    () =>
      body ? (
        <Modal width="sm" close={handleDismiss}>
          {!!body?.showDismiss && (
            <div className="flex justify-end items-center pt-6 pr-6">
              <button aria-label="Close modal" type="button" onClick={handleDismiss}>
                <CancelIconSvg />
              </button>
            </div>
          )}
          <div data-testid="error-modal" className={classNames(!!body?.showDismiss ? "px-6 pb-8" : "p-6", "space-y-4")}>
            {body?.status === "error" && (
              <div className="flex justify-center w-full item-center">
                <ErrorIconSvg width={50} height={50} />
              </div>
            )}
            {body?.status === "info" && (
              <div className="flex justify-center w-full item-center">
                <InfoIconSvg width={50} height={50} />
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold sm:text-2xl text-center">{body?.data.title}</h2>
              <p className="w-full text-center" dangerouslySetInnerHTML={{ __html: body?.data.description }}></p>
            </div>
            <div className="space-y-4 pt-4">
              <Button
                data-testid="status-modal-confirm-button"
                fullWidth
                text={body?.data.cta}
                onClick={handleConfirm}
                size="xl"
                color="primary"
                style={body?.status === "error" ? { backgroundColor: theme.colours["brand-grey"], border: "none", color: theme.colours.black } : {}}
              />
              {body?.cancelCallback && (
                <button data-testid="status-modal-cancel-button" onClick={handleCancel} className="w-full text-sm p-1.5 bg-transparent border-none">
                  {body?.data.dismissCta ?? "Cancel"}
                </button>
              )}
            </div>
          </div>
        </Modal>
      ) : null,
    [body, handleConfirm, handleDismiss, handleCancel]
  );

  useEffect(() => {
    if (!!body) {
      if (body.status === "info") {
        showInfo();
      }
      if (body.status === "error") {
        showError();
      }
      if (body.status === "success") {
        showSuccess();
      }
    } else if (!!modalComponent) {
      handleDismiss();
    }
  }, [body, hideError, hideSuccess, showError, showSuccess, handleDismiss, modalComponent, showInfo]);

  return {
    Modal: isVisibleError || isVisibleSuccess || isVisibleInfo ? modalComponent : null,
    setBody,
  };
};
